import React, { useState, useEffect } from 'react';
import { sendEmail } from '../utils/emailService';
import { EXPERTSCOM_API_URL } from '../config';
import { showSuccessAlert } from '../utils/alertService';

const culturePerks = [
  {
    title: 'Competitive Compensation',
    description: 'Attractive salary packages, performance bonuses, and yearly growth incentives.',
    icon: (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#c40a0a" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <line x1="12" y1="1" x2="12" y2="23" />
        <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
      </svg>
    )
  },
  {
    title: 'Continuous Learning',
    description: 'Mentorship opportunities, skill enhancement workshops, and certification support.',
    icon: (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#c40a0a" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z" />
        <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z" />
      </svg>
    )
  },
  {
    title: 'Collaborative Workplace',
    description: 'Work alongside friendly, industry-leading professionals in an open culture.',
    icon: (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#c40a0a" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
        <circle cx="9" cy="7" r="4" />
        <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
        <path d="M16 3.13a4 4 0 0 1 0 7.75" />
      </svg>
    )
  },
  {
    title: 'Career Advancement',
    description: 'Clear career progression paths and internal promotions based on merit.',
    icon: (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#c40a0a" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="23 6 13.5 15.5 8.5 10.5 1 18" />
        <polyline points="17 6 23 6 23 12" />
      </svg>
    )
  }
];

function CareersSections({ setView }) {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedDept, setSelectedDept] = useState('All');
  const [activeJobModal, setActiveJobModal] = useState(null);

  useEffect(() => {
    let isMounted = true;

    const fetchCareers = async () => {
      setLoading(true);
      setError(null);

      // Primary API endpoint and fallback endpoints
      const endpoints = [
        `${EXPERTSCOM_API_URL}/careers`,
        'https://api.expertdesignhub.com/expertscom/careers',
        'http://localhost:3000/expertscom/careers'
      ];

      // Remove duplicate URLs
      const uniqueEndpoints = [...new Set(endpoints)];

      let fetchedSuccessfully = false;

      for (const url of uniqueEndpoints) {
        try {
          const response = await fetch(url);
          if (!response.ok) {
            continue;
          }
          const data = await response.json();
          if (data && (data.success || Array.isArray(data.data) || Array.isArray(data))) {
            const jobList = Array.isArray(data.data) ? data.data : (Array.isArray(data) ? data : []);
            if (isMounted) {
              setJobs(jobList);
              setLoading(false);
              fetchedSuccessfully = true;
            }
            break;
          }
        } catch (err) {
          console.warn(`Attempt to fetch careers from ${url} failed, trying next fallback:`, err.message);
        }
      }

      if (!fetchedSuccessfully && isMounted) {
        setError('Unable to load job openings at this moment. Please check back shortly.');
        setLoading(false);
      }
    };

    fetchCareers();

    return () => {
      isMounted = false;
    };
  }, []);

  // Dynamically compute departments from fetched jobs
  const defaultDepartments = ['Software Engineering', 'BPO Services', 'Marketing', 'Design', 'IT Services'];
  const fetchedDepartments = jobs.map(j => j.department).filter(Boolean);
  const allDepartments = Array.from(new Set([...defaultDepartments, ...fetchedDepartments]));
  const departments = ['All', ...allDepartments];

  const filteredJobs = selectedDept === 'All' 
    ? jobs 
    : jobs.filter(job => job.department === selectedDept);

  const handleApplyNow = (job) => {
    const query = encodeURIComponent(job.title);
    const targetUrl = `/job-application/?jt=${query}`;
    if (setView) {
      if (window.location.pathname !== '/job-application/' || window.location.search !== `?jt=${query}`) {
        window.history.pushState({}, '', targetUrl);
      }
      setView('job-application');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      window.location.href = targetUrl;
    }
  };

  return (
    <div className="careers-page-wrapper">

      {/* 1. Why Join Us / Culture Section */}
      <section className="careers-culture-section">
        <div className="container">
          <div className="careers-section-header">
            <span className="careers-badge">
              Our Culture &amp; Perks
            </span>
            <h2 className="careers-title">
              Why Build Your Career at Experts Communication?
            </h2>
            <p className="careers-subtitle">
              We believe our people are our greatest strength. At Experts Communication, you will find a vibrant environment that encourages creativity, fosters teamwork, and celebrates every achievement.
            </p>
          </div>

          <div className="perks-grid">
            {culturePerks.map((perk, index) => (
              <div key={index} className="perk-card">
                <div className="perk-icon-box">
                  {perk.icon}
                </div>
                <h3 className="perk-card-title">{perk.title}</h3>
                <p className="perk-card-desc">{perk.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 2. Open Positions Section */}
      <section className="careers-openings-section">
        <div className="container">
          <div className="careers-section-header">
            <span className="careers-badge">
              Open Positions
            </span>
            <h2 className="careers-title">
              Explore Career Opportunities
            </h2>
            <p className="careers-subtitle">
              Find your role and take the next leap in your professional career.
            </p>
          </div>

          {/* Department Filter Pills */}
          <div className="filter-pills-wrapper">
            {departments.map(dept => (
              <button
                key={dept}
                onClick={() => setSelectedDept(dept)}
                className={`filter-pill ${selectedDept === dept ? 'active' : ''}`}
              >
                {dept}
              </button>
            ))}
          </div>

          {/* Jobs List */}
          <div className="jobs-list-container">
            {loading ? (
              Array.from({ length: 4 }).map((_, idx) => (
                <div key={idx} className="job-card job-card-skeleton">
                  <div className="job-card-info" style={{ width: '100%' }}>
                    <div className="job-card-tags" style={{ marginBottom: '1rem' }}>
                      <div className="skeleton-box" style={{ width: '120px', height: '26px', borderRadius: '6px' }}></div>
                      <div className="skeleton-box" style={{ width: '85px', height: '26px', borderRadius: '6px' }}></div>
                    </div>
                    <div className="skeleton-box" style={{ width: '75%', height: '26px', borderRadius: '6px', marginBottom: '1rem' }}></div>
                    <div className="skeleton-box" style={{ width: '100%', height: '14px', borderRadius: '4px', marginBottom: '0.5rem' }}></div>
                    <div className="skeleton-box" style={{ width: '92%', height: '14px', borderRadius: '4px', marginBottom: '0.5rem' }}></div>
                    <div className="skeleton-box" style={{ width: '65%', height: '14px', borderRadius: '4px', marginBottom: '1.25rem' }}></div>
                    <div className="job-meta" style={{ display: 'flex', gap: '1.2rem', marginTop: 'auto' }}>
                      <div className="skeleton-box" style={{ width: '140px', height: '18px', borderRadius: '4px' }}></div>
                      <div className="skeleton-box" style={{ width: '90px', height: '18px', borderRadius: '4px' }}></div>
                    </div>
                  </div>
                  <div className="skeleton-box" style={{ width: '130px', height: '42px', borderRadius: '8px', marginTop: '0.5rem' }}></div>
                </div>
              ))
            ) : error && jobs.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '3rem 1rem', color: '#64748b' }}>
                <p style={{ color: '#ef4444', marginBottom: '0.5rem', fontWeight: 600 }}>{error}</p>
                <p>Feel free to submit a direct application using the form below.</p>
              </div>
            ) : filteredJobs.length > 0 ? (
              filteredJobs.map(job => (
                <div key={job.id} className="job-card">
                  <div className="job-card-info">
                    <div className="job-card-tags">
                      <span className="dept-tag">{job.department}</span>
                      <span className="type-tag">{job.type}</span>
                    </div>
                    <h3 className="job-title">{job.title}</h3>
                    <p className="job-desc">{job.description}</p>
                    <div className="job-meta">
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#c40a0a" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                          <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                          <circle cx="12" cy="10" r="3" />
                        </svg>
                        {job.location}
                      </span>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#c40a0a" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                          <rect x="2" y="7" width="20" height="14" rx="2" ry="2" />
                          <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" />
                        </svg>
                        {job.experience}
                      </span>
                    </div>
                  </div>
                  <button
                    onClick={() => handleApplyNow(job)}
                    className="btn-apply-now"
                  >
                    Apply Now
                  </button>
                </div>
              ))
            ) : (
              <p className="no-jobs-text">No active positions found in this department.</p>
            )}
          </div>
        </div>
      </section>

      {/* 3. Application / General Contact Form */}
      <section className="careers-apply-section">
        <div className="container">
          <div className="careers-section-header">
            <span className="careers-badge">
              Direct Application
            </span>
            <h2 className="careers-title">
              Don't See a Relevant Role?
            </h2>
            <p className="careers-subtitle">
              Send us your resume and tell us how you can contribute to Experts Communication.
            </p>
          </div>

          <div className="careers-apply-wrapper">
            {/* Left Side Form */}
            <form 
              className="careers-form"
              onSubmit={async (e) => {
                e.preventDefault();
                const form = e.target;
                const name = form.elements[0]?.value || '';
                const email = form.elements[1]?.value || '';
                const phone = form.elements[2]?.value || '';
                const dept = form.elements[3]?.value || '';
                const notes = form.elements[4]?.value || '';

                await sendEmail({
                  form_type: 'Direct Career Resume / Application',
                  name: name,
                  from_name: name,
                  user_name: name,
                  email: email,
                  from_email: email,
                  user_email: email,
                  phone: phone,
                  user_phone: phone,
                  department: dept,
                  designation: dept,
                  message: `Direct Career Application:
Applicant Name: ${name}
Email: ${email}
Phone: ${phone}
Department of Interest: ${dept}
Notes / Portfolio: ${notes}`
                });

                showSuccessAlert(
                  'Application Received!',
                  'Thank you for submitting your application! Our HR team will review your profile and reach out shortly.'
                );
                form.reset();
              }}
            >
              <div className="form-grid-two">
                <div className="form-group-item">
                  <label>Full Name *</label>
                  <input 
                    type="text" 
                    required 
                    placeholder="John Doe" 
                  />
                </div>
                <div className="form-group-item">
                  <label>Email Address *</label>
                  <input 
                    type="email" 
                    required 
                    placeholder="john@example.com" 
                  />
                </div>
              </div>

              <div className="form-grid-two">
                <div className="form-group-item">
                  <label>Phone Number *</label>
                  <input 
                    type="tel" 
                    required 
                    placeholder="+92 (317) 777 0002" 
                  />
                </div>
                <div className="form-group-item">
                  <label>Department of Interest</label>
                  <select>
                    <option>Software Engineering</option>
                    <option>BPO Services</option>
                    <option>Digital Marketing</option>
                    <option>Design</option>
                    <option>IT Services &amp; Infrastructure</option>
                    <option>Other</option>
                  </select>
                </div>
              </div>

              <div className="form-group-item full-width-item">
                <label>Cover Note / Portfolio Link</label>
                <textarea 
                  rows="4" 
                  placeholder="Tell us briefly about your experience and skills..."
                ></textarea>
              </div>

              <button type="submit" className="btn-submit-app">
                SUBMIT APPLICATION
              </button>
            </form>

            {/* Right Side Image */}
            <div className="careers-apply-image-container">
              <img 
                src="/assets/images/experienced_professionals.png" 
                alt="Join Experts Communication Team" 
                className="careers-apply-img"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Apply Modal */}
      {activeJobModal && (
        <div className="careers-modal-backdrop">
          <div className="careers-modal-box">
            <button 
              onClick={() => setActiveJobModal(null)}
              className="careers-modal-close"
            >
              ×
            </button>
            <h3 className="modal-title">Apply for {activeJobModal.title}</h3>
            <p className="modal-subtitle">{activeJobModal.department} • {activeJobModal.location}</p>
            
            <form 
              className="modal-form"
              onSubmit={async (e) => {
                e.preventDefault();
                const form = e.target;
                const name = form.elements[0]?.value || '';
                const email = form.elements[1]?.value || '';
                const phone = form.elements[2]?.value || '';
                const note = form.elements[3]?.value || '';

                await sendEmail({
                  form_type: `Job Application: ${activeJobModal.title}`,
                  position: activeJobModal.title,
                  designation: activeJobModal.title,
                  department: activeJobModal.department,
                  name,
                  email,
                  phone,
                  message: `Candidate applied for: ${activeJobModal.title}
Department: ${activeJobModal.department}
Location: ${activeJobModal.location}
Name: ${name}
Email: ${email}
Phone: ${phone}
Cover Note: ${note}`
                });

                showSuccessAlert(
                  'Application Submitted!',
                  `Your application for ${activeJobModal.title} has been submitted successfully!`
                );
                setActiveJobModal(null);
              }}
            >
              <div className="form-group-item">
                <label>Your Name *</label>
                <input type="text" required placeholder="Your Name" />
              </div>
              <div className="form-group-item">
                <label>Your Email *</label>
                <input type="email" required placeholder="Your Email" />
              </div>
              <div className="form-group-item">
                <label>Phone Number *</label>
                <input type="tel" required placeholder="Phone Number" />
              </div>
              <div className="form-group-item">
                <label>Cover Note / LinkedIn Profile</label>
                <textarea rows="3" placeholder="Short introduction or LinkedIn profile"></textarea>
              </div>
              <button type="submit" className="btn-submit-app">
                Submit Application
              </button>
            </form>
          </div>
        </div>
      )}

    </div>
  );
}

export default CareersSections;
